<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useKanbanPipelinesStore } from 'dashboard/stores/kanbanPipelines';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import KanbanPipelineDialog from 'dashboard/components-next/Kanban/KanbanPipelineDialog.vue';

const { t } = useI18n();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const kanbanPipelinesStore = useKanbanPipelinesStore();

const pipelineDialogRef = ref(null);
const deleteDialogRef = ref(null);
const pipelineToDelete = ref(null);

const pipelines = computed(() => kanbanPipelinesStore.getPipelines);
const uiFlags = computed(() => kanbanPipelinesStore.getUIFlags);

onMounted(() => kanbanPipelinesStore.get());

const openBoard = pipeline => {
  router.push(accountScopedRoute('kanban_board', { pipelineId: pipeline.id }));
};

const openCreateDialog = () => pipelineDialogRef.value?.open();
const openEditDialog = pipeline => pipelineDialogRef.value?.open(pipeline);

const handleConfirm = async ({ id, name }) => {
  try {
    if (id) {
      await kanbanPipelinesStore.update({ id, name });
      useAlert(t('KANBAN.PIPELINES.EDIT.SUCCESS_MESSAGE'));
    } else {
      await kanbanPipelinesStore.create({ name });
      useAlert(t('KANBAN.PIPELINES.ADD.SUCCESS_MESSAGE'));
    }
    pipelineDialogRef.value?.onSuccess();
  } catch (error) {
    useAlert(
      id
        ? t('KANBAN.PIPELINES.EDIT.ERROR_MESSAGE')
        : t('KANBAN.PIPELINES.ADD.ERROR_MESSAGE')
    );
  }
};

const openDeleteDialog = pipeline => {
  pipelineToDelete.value = pipeline;
  deleteDialogRef.value?.open();
};

const handleDelete = async () => {
  try {
    await kanbanPipelinesStore.delete(pipelineToDelete.value.id);
    useAlert(t('KANBAN.PIPELINES.DELETE.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('KANBAN.PIPELINES.DELETE.ERROR_MESSAGE'));
  } finally {
    deleteDialogRef.value?.close();
  }
};
</script>

<template>
  <div class="flex flex-col w-full h-full gap-4 p-6 overflow-y-auto">
    <div class="flex items-center justify-between">
      <h1 class="text-xl font-medium text-n-slate-12">
        {{ t('KANBAN.PIPELINES.HEADER') }}
      </h1>
      <Button
        :label="t('KANBAN.PIPELINES.ADD.TITLE')"
        icon="i-lucide-plus"
        @click="openCreateDialog"
      />
    </div>

    <div
      v-if="!uiFlags.fetchingList && !pipelines.length"
      class="flex flex-col items-center justify-center gap-3 py-16 text-center"
    >
      <span class="text-base font-medium text-n-slate-12">
        {{ t('KANBAN.EMPTY_STATE.TITLE') }}
      </span>
      <span class="max-w-sm text-sm text-n-slate-11">
        {{ t('KANBAN.EMPTY_STATE.SUBTITLE') }}
      </span>
      <Button
        :label="t('KANBAN.EMPTY_STATE.ACTION')"
        icon="i-lucide-plus"
        @click="openCreateDialog"
      />
    </div>

    <div v-else class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      <div
        v-for="pipeline in pipelines"
        :key="pipeline.id"
        class="flex flex-col gap-3 p-4 border rounded-xl cursor-pointer border-n-weak bg-n-solid-2 hover:border-n-weak-hover"
        @click="openBoard(pipeline)"
      >
        <div class="flex items-center justify-between gap-2">
          <span class="text-sm font-medium truncate text-n-slate-12">
            {{ pipeline.name }}
          </span>
          <div class="flex items-center gap-1" @click.stop>
            <Button
              icon="i-lucide-pencil"
              slate
              xs
              variant="ghost"
              @click="openEditDialog(pipeline)"
            />
            <Button
              icon="i-lucide-trash"
              slate
              xs
              variant="ghost"
              @click="openDeleteDialog(pipeline)"
            />
          </div>
        </div>
      </div>
    </div>

    <KanbanPipelineDialog
      ref="pipelineDialogRef"
      :is-loading="uiFlags.creatingItem || uiFlags.updatingItem"
      @confirm="handleConfirm"
    />

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="t('KANBAN.PIPELINES.DELETE.TITLE')"
      :description="t('KANBAN.PIPELINES.DELETE.CONFIRM')"
      :is-loading="uiFlags.deletingItem"
      @confirm="handleDelete"
    />
  </div>
</template>
